// @ts-nocheck - Temporarily disable type checking during refactor
import { ModelFactory } from '../../common/$models'
import { VirtualModel } from '../../common/$virtualModels/VirtualModel'
import { generateSidebarLinksToOtherApps } from './generateSidebarLinksToOtherApps'
import type { LayoutMenuItem } from '../Layouts/menuTypes' // ★ Import new type

export type AppDefinitionMainMenu = any

export const genLinkWithModelNames = (modelNames: string[]): LayoutMenuItem[] => {
  return modelNames
    .map((modelName) => {
      try {
        // Ensure $core and models are available
        const modelDef = globalThis.$core?.$models?.[modelName]
        if (!modelDef) throw new Error(`Model not found: ${modelName}`)
        return genLinkWithModelDef(modelDef)
      } catch (e) {
        console.error(`Error generating link for model ${modelName}:`, e)
        return null // Return null for errors
      }
    })
    .filter((item) => item !== null) as LayoutMenuItem[] // Filter out nulls
}

export const genLinkWithModelDef = (
  modelDef: ModelFactory | VirtualModel,
  menuDef: Partial<AppDefinitionMainMenu> = {}, // Accept partial original def for extra props
): LayoutMenuItem | null => {
  try {
    if (!modelDef) return null
    const isVirtual = !!(modelDef as VirtualModel).baseModel
    const tableName = modelDef.tableName
    const modelName = modelDef.name || tableName // Use name if available (VirtualModel)

    return {
      id: `model-${modelName}`, // Generate an ID
      type: 'link',
      label: modelDef.tableLabel || tableName,
      to: isVirtual
        ? `/m/${(modelDef as VirtualModel).baseModel}?virtualModel=${modelName}`
        : `/m/${tableName}`,
      // Copy relevant properties from menuDef
      icon: menuDef.icon || '',
      cssClass: menuDef.cssClass,
      amountBadgeVisible: menuDef.amountBadgeVisible,
      amountBadgeTargetModel: menuDef.amountBadgeTargetModel,
      amountBadgeFilterCondition: menuDef.amountBadgeFilterCondition,
      amountBadgeStyle: menuDef.amountBadgeStyle,
      amountBadgeStyleWhenZero: menuDef.amountBadgeStyleWhenZero,
      useActiveLinkRegex: menuDef.useActiveLinkRegex,
      activeLinkRegex: menuDef.activeLinkRegex,
      activeLinkRegexFlags: menuDef.activeLinkRegexFlags,
      userRoleRestrictions: menuDef.userRoleRestrictions,
      _originalLinkType: isVirtual ? 'vmodel' : 'model',
    }
  } catch (e) {
    console.warn(`Error generating link for modelDef ${modelDef?.tableName}:`, e)
    return null
  }
}

// Link generation functions mapping original types to LayoutMenuItem
const linkGenFunc: {
  [key: string]: (menuDef: AppDefinitionMainMenu) => LayoutMenuItem | null
} = {
  model: (menuDef) => {
    const model = globalThis.$core?.$models?.[menuDef.linkTargetModel]
    return model ? genLinkWithModelDef(model, menuDef) : null
  },
  vmodel: (menuDef) => {
    const vmodel = globalThis.$core?.$virtualModels?.[menuDef.linkTargetVModel]
    return vmodel ? genLinkWithModelDef(vmodel, menuDef) : null
  },
  heading: (menuDef) => ({
    id: `heading-${menuDef.label?.replace(/\s+/g, '') ?? Math.random().toString(36).substring(7)}`, // Generate ID
    type: 'heading',
    label: menuDef.label ?? '', // Ensure label is string
    icon: menuDef.icon,
    isHeader: true,
    userRoleRestrictions: menuDef.userRoleRestrictions,
    _originalLinkType: 'heading',
  }),
  free: (menuDef) => ({
    id: `free-${menuDef.label?.replace(/\s+/g, '') ?? Math.random().toString(36).substring(7)}`,
    type: 'link',
    label: menuDef.label ?? '',
    to: menuDef.linkTarget,
    icon: menuDef.icon,
    cssClass: menuDef.cssClass,
    userRoleRestrictions: menuDef.userRoleRestrictions,
    useActiveLinkRegex: menuDef.useActiveLinkRegex,
    activeLinkRegex: menuDef.activeLinkRegex,
    activeLinkRegexFlags: menuDef.activeLinkRegexFlags,
    _originalLinkType: 'free',
  }),
  component: async (menuDef) => {
    try {
      const componentDefinition = await $core.$utils.executeStringDefinedFunction({
        functionString: menuDef.component,
        functionArgValues: {},
        errorThrow: true,
      })
      return {
        id: `component-${Math.random().toString(36).substring(7)}`,
        type: 'component',
        label: menuDef.label ?? 'Component', // Use label if provided
        icon: menuDef.icon,
        componentDefinition,
        cssClass: menuDef.cssClass,
        userRoleRestrictions: menuDef.userRoleRestrictions,
        _originalLinkType: 'component',
      }
    } catch (e) {
      console.error('Error executing component definition string:', e, menuDef.component)
      return null
    }
  },
  nested: async (menuDef) => {
    const children = await parseMainMenuDefinitionIntoLayoutMenuItems(
      menuDef.children ?? [],
    )
    // Only return nested item if it has a label and children
    if (!menuDef.label || children.length === 0) return null
    return {
      id: `nested-${menuDef.label?.replace(/\s+/g, '') ?? Math.random().toString(36).substring(7)}`,
      type: 'nested',
      label: menuDef.label,
      icon: menuDef.icon,
      children: children,
      cssClass: menuDef.cssClass,
      userRoleRestrictions: menuDef.userRoleRestrictions,
      _originalLinkType: 'nested',
    }
  },
}

/**
 * Parses the main menu definition from appDefinition into LayoutMenuItem format.
 * Includes filtering based on user roles.
 * @param menus The menu definition array from appDefinition.mainMenus
 * @returns An array of LayoutMenuItem suitable for the sidebar.
 */
export const parseMainMenuDefinitionIntoLayoutMenuItems = async (
  menus: AppDefinitionMainMenu[] | undefined | null,
): Promise<LayoutMenuItem[]> => {
  if (!menus) return []
  // 1. Filter based on user roles
  const roleFilteredMenus = filterOutMenuWithUserRoleRestrictions(menus)
  // 2. Map to LayoutMenuItem format
  const generatedLinks = await Promise.all(
    roleFilteredMenus.map(async (menuDef) => {
      if (linkGenFunc[menuDef.linkType]) {
        return {
          ...menuDef,
          ...(await linkGenFunc[menuDef.linkType](menuDef)),
        }
      } else {
        console.warn('Unknown menu linkType:', menuDef.linkType)
        return null
      }
    }),
  )
  return generatedLinks.filter((item) => item !== null) // Filter out nulls from failed parsing/generation
}

/**
 * Filters menu items based on the current user's roles.
 * @param menus Menu items to filter.
 * @returns Filtered menu items.
 */
const filterOutMenuWithUserRoleRestrictions = (
  menus: AppDefinitionMainMenu[],
): AppDefinitionMainMenu[] => {
  const userRoles = $core?.$embAuth?.user?.coreRoles || []
  const isAdmin = $core?.$embAuth?.user?.isAdmin === true

  return (
    menus?.filter((menu) => {
      // Always show if no restrictions or user is admin
      if (
        isAdmin ||
        !menu.userRoleRestrictions ||
        menu.userRoleRestrictions.length === 0
      ) {
        return true
      }
      // Check if user has at least one of the required roles
      const requiredRoles = Array.isArray(menu.userRoleRestrictions)
        ? menu.userRoleRestrictions
        : [menu.userRoleRestrictions] // Handle single string case just in case

      return requiredRoles.some((roleKey) => userRoles.includes(roleKey))
    }) || []
  )
}

/**
 * Generates sidebar links based on the current app definition,
 * including links to other accessible apps.
 * @param includeOtherAppLinks Whether to include links to other apps.
 * @returns An array of LayoutMenuItem.
 */
export const getSidebarLinksBasedOnAppDefinition = async (
  includeOtherAppLinks = true,
): Promise<LayoutMenuItem[]> => {
  const appDef = globalThis.$core?.$appDefinitionLoader?.appDefinition
  const menuLinks = await parseMainMenuDefinitionIntoLayoutMenuItems(appDef?.mainMenus)

  if (!includeOtherAppLinks) {
    return menuLinks
  }

  const linksToOtherApps = generateSidebarLinksToOtherApps()
  // // Add a heading before other app links if there are any
  if (linksToOtherApps.length > 0) {
    return menuLinks.concat(
      // { id: 'otherAppsHeading', type: 'heading', label: '他のアプリ', isHeader: true },
      ...linksToOtherApps,
    )
  }

  return menuLinks
}

/**
 * Gets the count for an amount badge based on model name and filter condition.
 * @param modelName Target model name.
 * @param filterCondition Filter condition string (JSON) or object.
 * @returns The count or null if an error occurs.
 */
export const getAmountBadgeCount = async (
  modelName: string,
  filterCondition: string | object | undefined,
): Promise<number | null> => {
  if (!modelName) return null
  try {
    const model = globalThis.$core?.$models?.[modelName]
    if (!model) throw new Error(`Badge target model not found: ${modelName}`)

    let parsedFilter = {}
    if (typeof filterCondition === 'string' && filterCondition) {
      try {
        parsedFilter = JSON.parse(filterCondition)
      } catch (e) {
        console.error(
          'Failed to parse amountBadgeFilterCondition JSON:',
          filterCondition,
          e,
        )
        parsedFilter = {} // Use empty filter on parse error
      }
    } else if (typeof filterCondition === 'object' && filterCondition !== null) {
      parsedFilter = filterCondition
    }

    return await model.countBy(parsedFilter)
  } catch (e) {
    console.error(`Error getting amount badge count for ${modelName}:`, e)
    return null
  }
}
